import { useMemo } from 'react';

import type { ListingStage } from '@/api/v4/project-listings.api';
import type { IconVariant } from '@/ui/icons/icon';

import { ListingStageName } from '../../../listing-stage/types';
import { useListingStages } from '../../../listing-stage/use-listing-stages.query';

import { useDealStatuses } from './use-deal-statuses';

export interface TabStage extends ListingStage {
  icon?: IconVariant;
}

export const useStageTabsConfig = () => {
  const { listingStages } = useListingStages();
  const dealStatuses = useDealStatuses();

  const putNotRelevantAsLast = (stages: ListingStage[]) => {
    const notRelevantStage = stages.find(
      stage => stage.name === ListingStageName.notRelevant,
    );
    const others = stages.filter(
      stage => stage.name !== ListingStageName.notRelevant,
    );

    return notRelevantStage ? [...others, notRelevantStage] : others;
  };

  const tabStages: TabStage[] = useMemo(() => {
    if (!listingStages) {
      return [];
    }

    const stagesForTabs = listingStages.filter(stage =>
      Object.values(ListingStageName).some(
        stageName => stageName === stage?.name,
      ),
    );

    const dealTabSortOrder =
      dealStatuses.length > 0
        ? Math.min(...dealStatuses.map(({ sortOrder }) => sortOrder))
        : undefined;

    const dealTab =
      dealTabSortOrder !== undefined
        ? {
            id: ListingStageName.deal,
            name: ListingStageName.deal,
            displayName: null,
            sortOrder: dealTabSortOrder,
          }
        : null;

    const allStages = putNotRelevantAsLast([
      ...stagesForTabs,
      ...(dealTab ? [dealTab] : []),
    ]);

    const finalStages = allStages.map(stage => ({
      ...stage,
      ...(stage.name === ListingStageName.notRelevant && {
        icon: 'CloseCircle' as IconVariant,
      }),
    }));

    return finalStages;
  }, [dealStatuses, listingStages]);

  return tabStages;
};
